import type { User } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { Box, Center, Flex } from '@chakra-ui/react';
import { EditIconGhost } from '@halo/ui-chat-react';
import dayjs from 'dayjs';
import type { UseFormReturn } from 'react-hook-form';
import { useCountries } from '@/scripts/hooks/useCountries';
// import { Gender } from '../ProfileDialog/Profile/Gender';
import { EditBase } from './EditBase';
import { EditBio } from './EditBio';
import { EditGender } from './EditGender';
import { EditUserName } from './EditUserName';
import { EditLocation } from './EditLocation';
import { EditBirthday } from './EditBirthday';

export interface FieldProps {
  form: UseFormReturn<FormValues>;
  user: User;
}

export type FormValues = {
  firstName: string;
  lastName: string;
  username: string;
  bio: string;
  gender: string;
  location: string;
  birthday: string;
};

export enum EditItem {
  Name = 'name',
  Username = 'username',
  Bio = 'bio',
  Gender = 'gender',
  Location = 'location',
  Hometown = 'hometown',
  Birthday = 'birthday',
}

export const useEditItems = (
  user: User,
  setEditFormVisible: (visible: boolean) => void,
  setEditItem: (item: EditItem) => void,
  handleUpdateProfileUser?: () => void
) => {
  const { t } = useTranslation();
  const { countryLabel } = useCountries(user?.location || '');
  const { client } = useAppStore();
  const HandelGender = useCallback(
    async (value: Partial<FormValues>) => {
      await client?.Api.user.setExtendUserProfiles({
        gender: String(value.gender),
      });

      handleUpdateProfileUser?.();
    },
    [client, handleUpdateProfileUser]
  );
  return [
    {
      title: null,
      items: [
        {
          label: t('profile.name'),
          value: user?.name,
          onClick: () => {
            setEditItem(EditItem.Name);
            setEditFormVisible(true);
          },
        },
        {
          label: t('profile.username'),
          value: `@${user?.username}`,
          onClick: () => {
            setEditItem(EditItem.Username);
            setEditFormVisible(true);
          },
        },
      ],
    },
    {
      title: t('profile.bio'),
      items: [
        {
          label: null,
          value: null,
          children: (
            <>
              <Flex
                bg="bg"
                py="3.5"
                px="2"
                gap="2.5"
                rounded="xl"
                alignItems="center"
                justifyContent="space-between"
                cursor="pointer"
                onClick={() => {
                  setEditFormVisible(true);
                  setEditItem(EditItem.Bio);
                }}
                css={{
                  '&:hover': {
                    '--show-edit-bio': 'block',
                  },
                }}
              >
                <Box
                  color="fg.subtle"
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  lineClamp="5"
                >
                  {user?.bio || t('profile.bioPlaceholder')}
                </Box>
                <Center flexShrink="0" display="var(--show-edit-bio, none)" color="fg.muted">
                  <EditIconGhost />
                </Center>
              </Flex>
            </>
          ),
          onClick: () => {
            setEditItem(EditItem.Bio);
            setEditFormVisible(true);
          },
        },
      ],
    },
    {
      title: t('profile.basicInformation'),
      items: [
        {
          label: t('profile.gender'),
          value: <EditGender user={user} onChange={HandelGender} />,
          onClick: () => {},
        },
        {
          label: t('profile.location'),
          value: countryLabel,
          onClick: () => {
            setEditItem(EditItem.Location);
            setEditFormVisible(true);
          },
        },
        // {
        //   label: 'Hometown',
        //   value: user?.hometown,
        //   onClick: () => {
        //     setEditItem(EditItem.Hometown);
        //     setEditFormVisible(true);
        //   },
        // },
        {
          label: t('profile.birthday'),
          value: user?.birthday ? dayjs(+user?.birthday).format('YYYY-MM-DD') : null,
          onClick: () => {
            setEditItem(EditItem.Birthday);
            setEditFormVisible(true);
          },
        },
      ],
    },
  ];
};

export const useFields = (
  item: EditItem,
  setShow: (show: boolean) => void,
  form: UseFormReturn<FormValues>,
  user: User,
  handleUpdateProfileUser?: () => void
) => {
  const { handleSubmit } = form;
  const { client } = useAppStore();
  // const { setProfileOpen } = useActionsStore();
  const [loading, setLoading] = useState(false);

  const props = useMemo(() => {
    return {
      user,
      form,
    };
  }, [user, form]);

  const fields = useMemo(() => {
    switch (item) {
      case EditItem.Name:
        return <EditBase {...props} />;
      case EditItem.Username:
        return <EditUserName {...props} />;
      case EditItem.Bio:
        return <EditBio {...props} />;
      // case EditItem.Gender:
      //   return <EditGender {...props} />;
      case EditItem.Location:
        return <EditLocation {...props} />;
      // case EditItem.Hometown:
      //   return <EditHometown {...props} />;
      case EditItem.Birthday:
        return <EditBirthday {...props} />;
    }
  }, [item, props]);

  const onSubmit = handleSubmit(async (data: any) => {
    setLoading(true);
    if (item === EditItem.Name) {
      try {
        const firstName = data.firstName.trim();
        const lastName = data.lastName?.trim() ?? '';
        // const name = lastName ? `${firstName} ${lastName}` : firstName;
        await client?.Api.user.setUserName(firstName, lastName);
        await client?.Api.user.queryUserInfoGlobal(user?.user_id);
        setShow(false);
      } finally {
        setLoading(false);
      }
    }

    if (item === EditItem.Username) {
      try {
        if (data.username) {
          await client?.Api.user.setUserNameId(data.username);
        }
        setShow(false);
      } finally {
        setLoading(false);
      }
    }

    if (item === EditItem.Bio) {
      try {
        // if (data.bio) {
        await client?.Api.user.setExtendUserProfiles({ bio: data.bio });
        // }
        setShow(false);
      } finally {
        setLoading(false);
      }
    }

    // if (item === EditItem.Gender) {
    //   try {
    //     if (data.gender?.[0]) {
    //       await client?.Api.user.setExtendUserProfiles({
    //         gender: String(data.gender[0]),
    //         gender_name: data.gender_name,
    //       });
    //     }
    //     setShow(false);
    //   } finally {
    //     setLoading(false);
    //   }
    // }

    if (item === EditItem.Location) {
      try {
        if (data.location?.[0]) {
          await client?.Api.user.setExtendUserProfiles({ location: data.location[0] });
        }
        setShow(false);
      } finally {
        setLoading(false);
      }
    }

    if (item === EditItem.Birthday) {
      try {
        const val = data.birthday && data.birthday > 0 ? String(data.birthday) : '';
        await client?.Api.user.setExtendUserProfiles({ birthday: val });
        setShow(false);
      } finally {
        setLoading(false);
      }
    }

    handleUpdateProfileUser?.();
    // setProfileOpen(false, false);
  });

  return { fields, onSubmit, loading };
};
